import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from "@angular/core";
import { Observable } from "rxjs";
import { SCFPrivileges, } from "../../scf-v2.service";
import { AbstractControl } from "@angular/forms";
import { utils } from "src/app/modules/libs/utils";
import { FormRestriction } from "src/app/services/forms/form-restriction";
import { SCFMaster } from "../../scf";
import { BaseComponent } from "src/app/common/base/base.component";
import { Resource } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import { Restriction } from "src/app/controls/restrictions/services/restriction";
import { FormViewState } from "src/app/common/models/form-view-state";
@Component({
  selector: "scf-v2-main-s2",
  templateUrl: "./scf-v2-main-s2.component.html",
  styleUrls: ["./scf-v2-main-s2.component.scss"],
})
export class ScfV2MainS2Component extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() scfMaster?: SCFMaster | null;
  @Input() disabled?: boolean | null = true;
  @Input() privileges?: SCFPrivileges | null;
  @Input() status?: FormViewState | null;

  @Output() changed = new EventEmitter<SCFMaster>();
  @ViewChild("restrictionInput") restrictionInput!: ElementRef<HTMLInputElement>;

  disableControls = true;

  locationIDs?: (number | undefined)[];
  formID?: number;

  public get restrictionsCtrl(): AbstractControl | null {
    return this.formGroup.get("restrictionsCtrl");
  }

  formRestrictions?: FormRestriction[];

  restrictions!: Restriction[];

  resources!: Resource[];
  resources$!: Observable<Resource[]>;

  filteredRestrictions!: Restriction[];
  uniqueRestrictions!: Restriction[];

  locationAddedIDs?: number[] | null;

  override ngOnDestroy(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  ngOnInit(): void {
  }

  refresh() {
    this.getValues();
  }

  getValues() {
    this.formRestrictions = [];
    if (this.status?.isViewingAmendment) {
      this.formRestrictions = this.utils.cloneDeep(this.scfMaster?.scfAmendments?.find(x => x.active)?.scfRestrictionsV2);
      const scfAmendment = this.scfMaster?.scfAmendments?.find(x => x.active);
      if (scfAmendment) {
        this.locationIDs = scfAmendment?.locations?.map((l) => l.id);
        this.formID = scfAmendment?.id;
      }
    } else if (this.scfMaster) {
      this.formRestrictions = this.scfMaster?.scfRestrictionsV2;
      this.locationIDs = this.scfMaster?.locations?.map((l) => l.id);
      this.formID = this.scfMaster.id;
    }
    this.formRestrictions?.map((r) => {
      if (r.restriction == null) {
        r.restriction = this.restrictions?.find((x) => x.id == r.restrictionID);
      }
    });
    // Unsaved rows (id 0/undefined) sort last so a new restriction stays at the
    // bottom where it was appended, instead of jumping to the top until saved.
    this.formRestrictions = this.formRestrictions?.sort((a, b) => (a.id || Number.MAX_SAFE_INTEGER) - (b.id || Number.MAX_SAFE_INTEGER));
  }

  /**
   * Writes the Section 2 restriction list back to the active SCF or amendment.
   * Marks the shared form dirty so Save/Submit sends the updated restriction payload.
   */
  setValues() {
    if (this.status?.isViewingAmendment) {
      const scfAmendment = this.scfMaster?.scfAmendments?.find((x) => x.active);
      if (scfAmendment)
        scfAmendment.scfRestrictionsV2 =
          this.formRestrictions;
    } else {
      if (this.scfMaster)
        this.scfMaster.scfRestrictionsV2 = this.formRestrictions;
    }
    // Mark Section 2 edits dirty so Save/Submit persists restriction changes.
    this.setFormDirty();
    if (this.scfMaster)
      this.changed.emit(this.scfMaster);
  }

  locationsAdded(e: number[]) {
    this.locationAddedIDs = e;
  }

  /**
   * Appends a free-text (custom) restriction to the list. Used by the tester
   * auto-populate tooling. A new array reference is assigned so the
   * form-restriction-table re-clones and keeps the entry as location-based
   * restrictions are added afterwards.
   */
  addCustomRestriction(text: string) {
    if (!text?.trim()) return;
    const restriction = {
      id: 0,
      formID: this.formID,
      text,
    } as FormRestriction;
    this.formRestrictions = [...(this.formRestrictions ?? []), restriction];
    this.setValues();
  }

  /** Clears all restrictions. Used by the tester auto-fill Reset option. */
  clearRestrictions() {
    this.formRestrictions = [];
    this.setValues();
  }

  restrictionsChanged(e: FormRestriction[]) {
    // Remove duplicates based on 'restrictionID'
    // const uniqueRestrictions = e.filter(
    //   (restriction, index, self) =>
    //     index === self.findIndex(r => r.restrictionID === restriction.restrictionID)
    // );
    const uniqueRestrictions = e.filter(
      (restriction, index, self) => {
        if (!restriction.restrictionID) {
          return true;
        }
        return index === self.findIndex(r => r.restrictionID === restriction.restrictionID);
      });

    this.formRestrictions = uniqueRestrictions;
    this.setValues();
  }

  diffRestrictions() {
    if (this.scfMaster && this.status?.isViewingAmendment) {
      this.formRestrictions?.map(r => { if (!r.text) r.text = null; });
      this.scfMaster.scfRestrictionsV2?.map(r => { if (!r.text) r.text = null; });
      // Sort copies only: these are the live arrays that drive the table's
      // display order, and an in-place sort here reshuffles the visible list.
      const current = utils.JSONstringify(
        this.formRestrictions
          ?.slice()
          .sort((a, b) =>
            utils.sort(a.restrictionID, b.restrictionID, true)
          )
          .map((l) => {
            return {
              restrictionID: l.restrictionID,
              text: l.text,
            } as FormRestriction;
          })
      )?.replace(/\s/g, "");
      const original = utils.JSONstringify(
        this.scfMaster?.scfRestrictionsV2
          ?.slice()
          .sort((a, b) =>
            utils.sort(a.restrictionID, b.restrictionID, true)
          )
          .map((l) => {
            return {
              restrictionID: l.restrictionID,
              text: l.text,
            } as FormRestriction;
          })
      )?.replace(/\s/g, "");
      this.formRestrictions?.map(r => {
        r.hilite = this.scfMaster?.scfRestrictionsV2?.find(p => p.restrictionID ? p.restrictionID == r.restrictionID : p.text == r.text) == null && this.status?.isViewingAmendment;
      });
      return current != original;
    }
    else return false;
  }

}
